import {
  pgTable,
  uuid,
  text,
  boolean,
  integer,
  jsonb,
  numeric,
  timestamp,
  index,
} from "drizzle-orm/pg-core";
import type { ProductTranslation, Translations } from "./translations.js";

export const products = pgTable(
  "products",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    sku: text("sku").notNull().unique(),
    slug: text("slug").notNull().unique(),
    translations: jsonb("translations")
      .$type<Translations<ProductTranslation>>()
      .notNull()
      .default({}),
    // Beverage metadata — nullable so non-alcohol products coexist
    abv: numeric("abv", { precision: 5, scale: 2 }),
    volumeMl: integer("volume_ml"),
    // Volume vs weight products (owner 2026-10-12). The amount lives in
    // volume_ml or weight_g per measure_type; measure_display picks how the
    // size renders (NULL = automatic — 75CL, or G|KG for weight). Unit price
    // follows the type: €/L for volume, €/KG for weight. CHECK-constrained.
    measureType: text("measure_type").$type<"volume" | "weight">().notNull().default("volume"),
    weightG: integer("weight_g"),
    measureDisplay: text("measure_display").$type<"l" | "cl" | "ml" | "kg" | "g">(),
    vintage: integer("vintage"),
    country: text("country"),
    region: text("region"),
    producer: text("producer"),
    // Classification
    isAlcohol: boolean("is_alcohol").notNull().default(true),
    requiresAgeVerification: boolean("requires_age_verification").notNull().default(true),
    requiresDeposit: boolean("requires_deposit").notNull().default(false),
    active: boolean("active").notNull().default(true),
    barcode: text("barcode"),
    serveTemp: text("serve_temp"),
    pdfUrl: text("pdf_url"),
    videoUrl: text("video_url"),
    strengthScale: integer("strength_scale"),
    typeScale: integer("type_scale"),
    // Units per full package ("Iepakojumā: N gab."), mirrored from Moneo
    // items cust_skiepak by the stock sync. NULL/0 = unknown → hidden.
    packSize: integer("pack_size"),
    salePrice: numeric("sale_price", { precision: 13, scale: 2 }),
    popularity: integer("popularity").notNull().default(0),
    shortDescription: text("short_description"),
    featured: boolean("featured").notNull().default(false),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    activeIdx: index("products_active_idx").on(t.active),
  })
);

export type Product = typeof products.$inferSelect;
export type NewProduct = typeof products.$inferInsert;
